import { Box, Divider, Typography, useTheme } from '@mui/material';
import ActionButtons from 'components/action-buttons';
import Mui from 'materials';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import EditFeedInventory from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/feed-inventory/edit-feed-inventory';
import FeedInventory from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/feed-inventory/feed-inventory';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import { useUpdateOFDSettings } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/hooks/use-update-ofd-settings';
import { UpdateManualInventoryInput } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/ofd-settings-input.type';
import { HeaderWrapper } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/styled-components';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.feedInventoryTab';

const FeedInventoryTab = () => {
  const theme = useTheme();
  const { updateFeedInventory } = useUpdateOFDSettings();
  const {
    getOfdSettingsQuery: { data: ofdSettings, isLoading: isOfdSettingsLoading },
  } = useInputSettingsState();

  const { createInputSettingsEvent } = useGoogleTagManager();
  const [edit, setEdit] = useState(false);
  const handleCancel = () => {
    setEdit(false);
  };

  const handleEdit = (edited: boolean) => setEdit(edited);

  const defaultValues: UpdateManualInventoryInput = useMemo(() => {
    return {
      inventory: ofdSettings?.inventoryExplanation.startingInventory ?? 0,
      afterDelivery: ofdSettings?.manualInventory?.afterDelivery ?? null,
    };
  }, [ofdSettings]);

  const { control, handleSubmit, setValue, getValues } = useForm<UpdateManualInventoryInput>({
    defaultValues: defaultValues,
  });

  const handleUpdateFeedInventory = (data: UpdateManualInventoryInput) => {
    updateFeedInventory.mutate(data, {
      onSuccess: (data) => {
        setEdit(false);
        if (ofdSettings) {
          let inventoryMeasured: '' | 'before delivery' | 'after delivery' = '';
          const afterDelivery = getValues('afterDelivery');

          switch (afterDelivery) {
            case true:
              inventoryMeasured = 'after delivery';
              break;
            case false:
              inventoryMeasured = 'before delivery';
              break;
            case null:
            default:
              break;
          }

          createInputSettingsEvent('save_feed_inventory', {
            oldValue1: String(ofdSettings.inventoryExplanation.startingInventory),
            newValue1: String(getValues('inventory')),
            section: 'inputSettings',
            consumptionRate: String(ofdSettings.currentConsumptionRate),
            inventoryMeasured: inventoryMeasured,
          });
        }
      },
    });
  };

  return (
    <>
      <Mui.Paper sx={{ minHeight: 600, p: 6 }}>
        <HeaderWrapper>
          <Box>
            <Typography variant="h3" fontWeight={700} mb={2}>
              <FormattedMessage id={`${PAGE_NAME_ID}.label`} />
            </Typography>
            <Typography variant="body1" mb={4}>
              <FormattedMessage id={`${PAGE_NAME_ID}.headerText`} />
            </Typography>
          </Box>
          <Box display={'flex'} gap={4}>
            <ActionButtons
              isEditing={edit}
              onEdit={handleEdit}
              onCancel={handleCancel}
              onSave={handleSubmit(handleUpdateFeedInventory)}
              isLoading={updateFeedInventory.isPending}
            />
          </Box>
        </HeaderWrapper>
        {edit ? (
          <>
            <Divider sx={{ borderColor: theme.palette.darkBlue.o25, margin: theme.spacing(8, 0) }} />
            <EditFeedInventory isDialog={false} control={control} setValue={setValue} />
          </>
        ) : (
          <>
            <FeedInventory isDialog={false} />
          </>
        )}
      </Mui.Paper>
    </>
  );
};

export default FeedInventoryTab;
